import React from 'react';
import { Mail, Phone, MapPin, Clock } from 'lucide-react';
import { companyInfo } from '../data/mock';
import { useTheme } from '../context/ThemeContext';

const ContactSection = () => {
  const { theme } = useTheme();

  return (
    <section id="contact" className={`relative py-16 sm:py-20 lg:py-24 transition-colors duration-300 ${
      theme === 'dark' ? 'bg-[#0a0a0a]' : 'bg-white'
    }`}>
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-[7.6923%]">
        {/* Section Header */}
        <div className="text-center mb-10 sm:mb-12 lg:mb-14">
          <div className={`inline-flex items-center gap-2 px-3 sm:px-4 py-2 mb-4 sm:mb-6 border ${
            theme === 'dark' ? 'border-white/20 bg-white/5' : 'border-gray-200 bg-gray-50'
          }`}>
            <span className={`text-xs sm:text-sm font-medium tracking-wide uppercase ${
              theme === 'dark' ? 'text-white/70' : 'text-gray-600'
            }`}>Contact</span>
          </div>
          <h2 className={`text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4 ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            Let's Work Together
          </h2>
          <p className={`text-base sm:text-lg max-w-[600px] mx-auto ${
            theme === 'dark' ? 'text-white/60' : 'text-gray-600'
          }`}>
            Have questions or need IT talent? Get in touch with us today.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Contact Info */}
          <div>
            <h3 className={`text-xl sm:text-2xl font-bold mb-6 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>Contact Information</h3>
            
            <div className="space-y-4 mb-8">
              <a href={`mailto:${companyInfo.email}`}
                className={`flex items-start gap-4 p-4 sm:p-5 transition-all duration-300 ${
                  theme === 'dark' 
                    ? 'bg-black border border-white/10 hover:border-[#00FFD1]/40' 
                    : 'bg-gray-50 border border-gray-200 hover:border-[#00997A]/40'
                }`}
              >
                <div className={`p-2.5 sm:p-3 ${
                  theme === 'dark' ? 'bg-[#00FFD1]/10' : 'bg-[#00997A]/10'
                }`}>
                  <Mail className={`w-5 h-5 sm:w-6 sm:h-6 ${
                    theme === 'dark' ? 'text-[#00FFD1]' : 'text-[#00997A]'
                  }`} />
                </div>
                <div>
                  <p className={`text-xs sm:text-sm mb-1 ${theme === 'dark' ? 'text-white/50' : 'text-gray-500'}`}>Email</p>
                  <p className={`text-sm sm:text-base ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    {companyInfo.email}
                  </p>
                </div>
              </a>

              <a href={`tel:${companyInfo.phone}`}
                className={`flex items-start gap-4 p-4 sm:p-5 transition-all duration-300 ${
                  theme === 'dark' 
                    ? 'bg-black border border-white/10 hover:border-[#00FFD1]/40' 
                    : 'bg-gray-50 border border-gray-200 hover:border-[#00997A]/40'
                }`}
              >
                <div className={`p-2.5 sm:p-3 ${
                  theme === 'dark' ? 'bg-[#00FFD1]/10' : 'bg-[#00997A]/10'
                }`}>
                  <Phone className={`w-5 h-5 sm:w-6 sm:h-6 ${
                    theme === 'dark' ? 'text-[#00FFD1]' : 'text-[#00997A]'
                  }`} />
                </div>
                <div>
                  <p className={`text-xs sm:text-sm mb-1 ${theme === 'dark' ? 'text-white/50' : 'text-gray-500'}`}>Phone</p>
                  <p className={`text-sm sm:text-base ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    {companyInfo.phone}
                  </p>
                </div>
              </a>

              <div className={`flex items-start gap-4 p-4 sm:p-5 ${
                theme === 'dark' ? 'bg-black border border-white/10' : 'bg-gray-50 border border-gray-200'
              }`}>
                <div className={`p-2.5 sm:p-3 ${
                  theme === 'dark' ? 'bg-[#00FFD1]/10' : 'bg-[#00997A]/10'
                }`}>
                  <MapPin className={`w-5 h-5 sm:w-6 sm:h-6 ${
                    theme === 'dark' ? 'text-[#00FFD1]' : 'text-[#00997A]'
                  }`} />
                </div>
                <div>
                  <p className={`text-xs sm:text-sm mb-1 ${theme === 'dark' ? 'text-white/50' : 'text-gray-500'}`}>Location</p>
                  <p className={`text-sm sm:text-base ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    {companyInfo.location}
                  </p>
                </div>
              </div>
            </div>

            <div className={`flex items-center gap-3 p-4 ${
              theme === 'dark' ? 'bg-[#00FFD1]/5 border border-[#00FFD1]/20' : 'bg-[#00997A]/5 border border-[#00997A]/20'
            }`}>
              <Clock className={`w-5 h-5 ${theme === 'dark' ? 'text-[#00FFD1]' : 'text-[#00997A]'}`} />
              <p className={`text-sm ${theme === 'dark' ? 'text-white/70' : 'text-gray-600'}`}>
                {companyInfo.hours}
              </p>
            </div>
          </div>

          {/* GoHighLevel Contact Form */}
          <div className="p-5 sm:p-6 lg:p-8 bg-white border border-gray-200 shadow-sm">
            <h3 className="text-xl sm:text-2xl font-bold mb-1 text-gray-900">Send Us a Message</h3>
            <p className="text-sm mb-6 text-gray-500">
              Fill out the form and we'll get back to you shortly
            </p>
            
            <div className="w-full" style={{ minHeight: '516px' }}>
              <iframe
                src="https://api.projectelevate.biz/widget/form/80Skg9Nx38jfZ5f4vLN9"
                style={{ width: '100%', height: '516px', border: 'none', borderRadius: '3px' }}
                id="inline-80Skg9Nx38jfZ5f4vLN9"
                data-layout="{'id':'INLINE'}"
                data-trigger-type="alwaysShow"
                data-trigger-value=""
                data-activation-type="alwaysActivated"
                data-activation-value=""
                data-deactivation-type="neverDeactivate"
                data-deactivation-value=""
                data-form-name="Contact Us"
                data-height="516"
                data-layout-iframe-id="inline-80Skg9Nx38jfZ5f4vLN9"
                data-form-id="80Skg9Nx38jfZ5f4vLN9"
                title="Contact Us"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
